import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
} from '@chakra-ui/react';
import { FaWifi, FaMobile, FaExclamationTriangle } from 'react-icons/fa';
import { useState } from 'react';

interface MobileDownloadWarningProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  sizeMB: number;
  shouldWarnDataUsage: boolean;
  shouldWarnLargeSize: boolean;
  networkType: string;
}

const MobileDownloadWarning = ({
  isOpen,
  onClose,
  onContinue,
  sizeMB,
  shouldWarnDataUsage,
  shouldWarnLargeSize,
  networkType: _networkType
}: MobileDownloadWarningProps) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleContinue = () => {
    // If user checked "don't show again" and this is only a data usage warning (not large size)
    if (dontShowAgain && shouldWarnDataUsage && !shouldWarnLargeSize) {
      sessionStorage.setItem('hideDataWarnings', 'true');
    }
    onContinue();
    onClose();
  };

  const getWarningType = () => {
    if (shouldWarnLargeSize) return 'error';
    if (shouldWarnDataUsage) return 'warning';
    return 'info';
  };

  const getWarningIcon = () => {
    if (shouldWarnLargeSize) return FaExclamationTriangle;
    if (shouldWarnDataUsage) return FaMobile;
    return FaWifi;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent bg="rgba(20, 20, 30, 0.95)" borderRadius="xl" p={4} mx={4}>
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Icon as={getWarningIcon()} color={getWarningType() === 'error' ? 'red.400' : getWarningType() === 'warning' ? 'orange.400' : 'blue.400'} />
            <Text fontSize="lg" fontWeight="bold">
              {shouldWarnLargeSize ? 'Large Download Warning' : shouldWarnDataUsage ? 'Mobile Download Notice' : 'Download Notice'}
            </Text>
          </HStack>
        </ModalHeader>

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <Alert status={getWarningType()} borderRadius="md" bg={`${getWarningType()}.50`} border="1px solid" borderColor={`${getWarningType()}.200`}>
              <AlertIcon />
              <AlertDescription fontSize="sm">
                You&apos;re about to download ~<strong>{sizeMB}MB</strong> of audio files.<br/>
                Make sure you&apos;re on WiFi to save mobile data.
              </AlertDescription>
            </Alert>

            {/* Show checkbox only for data usage warnings that are not part of large size warnings */}
            {shouldWarnDataUsage && !shouldWarnLargeSize && (
              <Checkbox
                isChecked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                colorScheme="blue"
                size="sm"
              >
                <Text fontSize="sm" color="gray.300">
                  Don&apos;t show data warnings for this session
                </Text>
              </Checkbox>
            )}

            {shouldWarnLargeSize && (
              <VStack spacing={2} align="stretch">
                <Text fontSize="sm" color="gray.300">
                  Downloads over 200MB may fail on mobile devices due to browser memory limitations.
                  Consider using a desktop computer instead.
                </Text>
              </VStack>
            )}


          </VStack>
        </ModalBody>

        <ModalFooter pt={4}>
          <HStack spacing={3} width="100%">
            <Button 
              variant="outline" 
              onClick={onClose}
              flex={1}
              size="md"
            >
              Cancel
            </Button>
            <Button 
              colorScheme={shouldWarnLargeSize ? 'red' : shouldWarnDataUsage ? 'orange' : 'blue'}
              onClick={handleContinue}
              flex={1}
              size="md"
            >
              {shouldWarnLargeSize ? 'Try Anyway' : 'Continue'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MobileDownloadWarning;
